export const HTTP_METHODS = [
  'GET',
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
  'HEAD',
  'OPTIONS',
] as const;

export type THttpMethod = typeof HTTP_METHODS[number];

export enum EHttpStatusCode {
  // 1xx Informational
  CONTINUE_100 = 100,
  SWITCHING_PROTOCOLS_101 = 101,
  PROCESSING_102 = 102,
  EARLY_HINTS_103 = 103,

  // 2xx Success
  OK_200 = 200,
  CREATED_201 = 201,
  ACCEPTED_202 = 202,
  NON_AUTHORITATIVE_INFORMATION_203 = 203,
  NO_CONTENT_204 = 204,
  RESET_CONTENT_205 = 205,
  PARTIAL_CONTENT_206 = 206,
  MULTI_STATUS_207 = 207,
  ALREADY_REPORTED_208 = 208,
  IM_USED_226 = 226,

  // 3xx Redirection
  MULTIPLE_CHOICES_300 = 300,
  MOVED_PERMANENTLY_301 = 301,
  FOUND_302 = 302,
  SEE_OTHER_303 = 303,
  NOT_MODIFIED_304 = 304,
  USE_PROXY_305 = 305,
  TEMPORARY_REDIRECT_307 = 307,
  PERMANENT_REDIRECT_308 = 308,

  // 4xx Client Errors
  BAD_REQUEST_400 = 400,
  UNAUTHORIZED_401 = 401,
  PAYMENT_REQUIRED_402 = 402,
  FORBIDDEN_403 = 403,
  NOT_FOUND_404 = 404,
  METHOD_NOT_ALLOWED_405 = 405,
  NOT_ACCEPTABLE_406 = 406,
  PROXY_AUTHENTICATION_REQUIRED_407 = 407,
  REQUEST_TIMEOUT_408 = 408,
  CONFLICT_409 = 409,
  GONE_410 = 410,
  LENGTH_REQUIRED_411 = 411,
  PRECONDITION_FAILED_412 = 412,
  CONTENT_TOO_LARGE_413 = 413,
  URI_TOO_LONG_414 = 414,
  UNSUPPORTED_MEDIA_TYPE_415 = 415,
  RANGE_NOT_SATISFIABLE_416 = 416,
  EXPECTATION_FAILED_417 = 417,
  IM_A_TEAPOT_418 = 418,
  MISDIRECTED_REQUEST_421 = 421,
  UNPROCESSABLE_CONTENT_422 = 422,
  LOCKED_423 = 423,
  FAILED_DEPENDENCY_424 = 424,
  TOO_EARLY_425 = 425,
  UPGRADE_REQUIRED_426 = 426,
  PRECONDITION_REQUIRED_428 = 428,
  TOO_MANY_REQUESTS_429 = 429,
  REQUEST_HEADER_FIELDS_TOO_LARGE_431 = 431,
  UNAVAILABLE_FOR_LEGAL_REASONS_451 = 451,

  // 5xx Server Errors
  INTERNAL_SERVER_ERROR_500 = 500,
  NOT_IMPLEMENTED_501 = 501,
  BAD_GATEWAY_502 = 502,
  SERVICE_UNAVAILABLE_503 = 503,
  GATEWAY_TIMEOUT_504 = 504,
  HTTP_VERSION_NOT_SUPPORTED_505 = 505,
  VARIANT_ALSO_NEGOTIATES_506 = 506,
  INSUFFICIENT_STORAGE_507 = 507,
  LOOP_DETECTED_508 = 508,
  NOT_EXTENDED_510 = 510,
  NETWORK_AUTHENTICATION_REQUIRED_511 = 511,
}

export enum EHttpStatusGroup {
  Informational = '1xx',
  Success = '2xx',
  Redirection = '3xx',
  ClientError = '4xx',
  ServerError = '5xx',
}

export const HTTP_STATUS_GROUPS = {
  [EHttpStatusGroup.Informational]: [
    EHttpStatusCode.CONTINUE_100,
    EHttpStatusCode.SWITCHING_PROTOCOLS_101,
    EHttpStatusCode.PROCESSING_102,
    EHttpStatusCode.EARLY_HINTS_103,
  ] as const,
  [EHttpStatusGroup.Success]: [
    EHttpStatusCode.OK_200,
    EHttpStatusCode.CREATED_201,
    EHttpStatusCode.ACCEPTED_202,
    EHttpStatusCode.NON_AUTHORITATIVE_INFORMATION_203,
    EHttpStatusCode.NO_CONTENT_204,
    EHttpStatusCode.RESET_CONTENT_205,
    EHttpStatusCode.PARTIAL_CONTENT_206,
    EHttpStatusCode.MULTI_STATUS_207,
    EHttpStatusCode.ALREADY_REPORTED_208,
    EHttpStatusCode.IM_USED_226,
  ] as const,
  [EHttpStatusGroup.Redirection]: [
    EHttpStatusCode.MULTIPLE_CHOICES_300,
    EHttpStatusCode.MOVED_PERMANENTLY_301,
    EHttpStatusCode.FOUND_302,
    EHttpStatusCode.SEE_OTHER_303,
    EHttpStatusCode.NOT_MODIFIED_304,
    EHttpStatusCode.USE_PROXY_305,
    EHttpStatusCode.TEMPORARY_REDIRECT_307,
    EHttpStatusCode.PERMANENT_REDIRECT_308,
  ] as const,
  [EHttpStatusGroup.ClientError]: [
    EHttpStatusCode.BAD_REQUEST_400,
    EHttpStatusCode.UNAUTHORIZED_401,
    EHttpStatusCode.PAYMENT_REQUIRED_402,
    EHttpStatusCode.FORBIDDEN_403,
    EHttpStatusCode.NOT_FOUND_404,
    EHttpStatusCode.METHOD_NOT_ALLOWED_405,
    EHttpStatusCode.NOT_ACCEPTABLE_406,
    EHttpStatusCode.PROXY_AUTHENTICATION_REQUIRED_407,
    EHttpStatusCode.REQUEST_TIMEOUT_408,
    EHttpStatusCode.CONFLICT_409,
    EHttpStatusCode.GONE_410,
    EHttpStatusCode.LENGTH_REQUIRED_411,
    EHttpStatusCode.PRECONDITION_FAILED_412,
    EHttpStatusCode.CONTENT_TOO_LARGE_413,
    EHttpStatusCode.URI_TOO_LONG_414,
    EHttpStatusCode.UNSUPPORTED_MEDIA_TYPE_415,
    EHttpStatusCode.RANGE_NOT_SATISFIABLE_416,
    EHttpStatusCode.EXPECTATION_FAILED_417,
    EHttpStatusCode.IM_A_TEAPOT_418,
    EHttpStatusCode.MISDIRECTED_REQUEST_421,
    EHttpStatusCode.UNPROCESSABLE_CONTENT_422,
    EHttpStatusCode.LOCKED_423,
    EHttpStatusCode.FAILED_DEPENDENCY_424,
    EHttpStatusCode.TOO_EARLY_425,
    EHttpStatusCode.UPGRADE_REQUIRED_426,
    EHttpStatusCode.PRECONDITION_REQUIRED_428,
    EHttpStatusCode.TOO_MANY_REQUESTS_429,
    EHttpStatusCode.REQUEST_HEADER_FIELDS_TOO_LARGE_431,
    EHttpStatusCode.UNAVAILABLE_FOR_LEGAL_REASONS_451,
  ] as const,
  [EHttpStatusGroup.ServerError]: [
    EHttpStatusCode.INTERNAL_SERVER_ERROR_500,
    EHttpStatusCode.NOT_IMPLEMENTED_501,
    EHttpStatusCode.BAD_GATEWAY_502,
    EHttpStatusCode.SERVICE_UNAVAILABLE_503,
    EHttpStatusCode.GATEWAY_TIMEOUT_504,
    EHttpStatusCode.HTTP_VERSION_NOT_SUPPORTED_505,
    EHttpStatusCode.VARIANT_ALSO_NEGOTIATES_506,
    EHttpStatusCode.INSUFFICIENT_STORAGE_507,
    EHttpStatusCode.LOOP_DETECTED_508,
    EHttpStatusCode.NOT_EXTENDED_510,
    EHttpStatusCode.NETWORK_AUTHENTICATION_REQUIRED_511,
  ] as const,
} as const;
